import { Vector3 } from 'three/webgpu';
import { mulberry32 } from '../math/rng';
import {
  EROSION_ROUNDING,
  EROSION_SLOPE_CAP,
  type NoiseLayer,
  octaveTaper,
  type TerrainProfile,
} from './TerrainProfile';

/**
 * The terrain's natural height on the CPU: the same recipe TileGenerator runs on the GPU
 * (without cave mouths), for a sphere hollow. For looking before the world is built:
 * choosing where caves open, where to start. It follows the GPU's maths step for step
 * (gradient noise with derivatives, fBm, ridges with erosion-like damping, the land mask),
 * so it agrees with the generated ground to within its float rounding.
 */

/** Noise lattice period (as terrainNoise's). */
const PERIOD = 289;
/** Ridged noise: a smooth |n| (as TileGenerator's RIDGE_EPS). */
const RIDGE_EPS = 0.03;
const RIDGE_NORM = 1 / (Math.sqrt(1 + RIDGE_EPS * RIDGE_EPS) - RIDGE_EPS);

export interface GroundSample {
  /** Height above sea level (m); negative under the water. */
  height: number;
  /** Steepness: the tangential gradient's length (0 flat, 1 is 45°). */
  slope: number;
  /** With erosion: −1 in a gully's bottom … 1 on the crest between two, 0 on flat ground. */
  ridge?: number;
}

/** A value and its gradient (per metre). */
type V4 = [number, number, number, number];

export class TerrainHeights {
  private readonly seedOffset: Vector3;
  private readonly p = new Vector3();

  constructor(
    readonly profile: TerrainProfile,
    readonly seed: number,
    readonly radius: number,
  ) {
    const rnd = mulberry32(seed);
    this.seedOffset = new Vector3(rnd() * 1e6, rnd() * 1e6, rnd() * 1e6);
  }

  /**
   * The ground at unit direction `dir` (hollow frame), band-limited like a tile whose texels
   * are `texel` metres (octaves finer than a few texels fade out, as on the GPU).
   */
  sample(dir: Vector3, texel = 1, out: GroundSample = { height: 0, slope: 0 }): GroundSample {
    const pr = this.profile;
    const p = this.p.copy(dir).multiplyScalar(this.radius).add(this.seedOffset);
    const wFine = (lambda: number) => smoothstep(2 * texel, 4 * texel, lambda);
    const octave = (f: number): V4 => {
      const n = gnoised(posMod(p.x * f, PERIOD), posMod(p.y * f, PERIOD), posMod(p.z * f, PERIOD));
      return [n[0], n[1] * f, n[2] * f, n[3] * f];
    };
    // `local` layers' finer octaves are tapered (see Smoothing); the normalising total isn't.
    const fbm = (layer: NoiseLayer, local = false): V4 => {
      const sum: V4 = [0, 0, 0, 0];
      let amp = 1;
      let f = 1 / layer.wavelength;
      let total = 0;
      for (let i = 0; i < layer.octaves; i++) {
        const w = wFine(1 / f) * amp * (local ? octaveTaper(pr, 1 / f) : 1);
        if (w > 0) {
          const n = octave(f);
          for (let c = 0; c < 4; c++) sum[c] += n[c] * w;
        }
        total += amp;
        amp *= layer.gain;
        f *= layer.lacunarity;
      }
      return sum.map((v) => v / total) as V4;
    };
    const ridged = (layer: NoiseLayer, damping: number): V4 => {
      const sum: V4 = [0, 0, 0, 0];
      let amp = 1;
      let f = 1 / layer.wavelength;
      let total = 0;
      for (let i = 0; i < layer.octaves; i++) {
        const wf = wFine(1 / f) * octaveTaper(pr, 1 / f);
        if (wf > 0) {
          const n = octave(f);
          const sa = Math.sqrt(n[0] * n[0] + RIDGE_EPS * RIDGE_EPS);
          const r = 1 - (sa - RIDGE_EPS) * RIDGE_NORM;
          const k = (n[0] / sa) * -RIDGE_NORM;
          const r2: V4 = [r * r, n[1] * k * 2 * r, n[2] * k * 2 * r, n[3] * k * 2 * r];
          const gx = sum[1] * layer.wavelength;
          const gy = sum[2] * layer.wavelength;
          const gz = sum[3] * layer.wavelength;
          const damp = 1 / (1 + (gx * gx + gy * gy + gz * gz) * damping * 0.02);
          const w = wf * damp * amp;
          for (let c = 0; c < 4; c++) sum[c] += r2[c] * w;
        }
        total += amp;
        amp *= layer.gain;
        f *= layer.lacunarity;
      }
      return sum.map((v) => v / total) as V4;
    };
    const smoothD = (e0: number, e1: number, a: V4): V4 => {
      const t = Math.min(Math.max((a[0] - e0) / (e1 - e0), 0), 1);
      const dv = (t * (1 - t) * 6) / (e1 - e0);
      return [t * t * (3 - 2 * t), a[1] * dv, a[2] * dv, a[3] * dv];
    };

    const cont = fbm(pr.continents);
    const mask = fbm(pr.mountainMask);
    const ridge = ridged(pr.ridges, pr.ridgeDamping);
    const hills = fbm(pr.hills, true);
    const detail = fbm(pr.detail, true);
    // Mountains only rise on land and inside the mask.
    const land: V4 = [cont[0] + pr.landBias, cont[1], cont[2], cont[3]];
    const landness = smoothD(-0.05, 0.25, land);
    const m = smoothD(-0.1, 0.35, mask);
    const mm: V4 = [
      m[0] * landness[0],
      m[1] * landness[0] + landness[1] * m[0],
      m[2] * landness[0] + landness[2] * m[0],
      m[3] * landness[0] + landness[3] * m[0],
    ];
    // The land's shape, before erosion and the fine detail.
    const h: V4 = [0, 0, 0, 0];
    for (let c = 0; c < 4; c++) {
      const mountains = (c === 0 ? mm[0] * ridge[0] : mm[c] * ridge[0] + ridge[c] * mm[0]) * pr.ridgeHeight;
      h[c] = land[c] * pr.continentHeight + mountains + hills[c] * pr.hillHeight;
    }
    const e = pr.erosion;
    let ridgeMap = 0;
    if (e) {
      // Gullies cut into the land's slope, across it: stripes whose waves run along the ground
      // as eroded so far, so each octave's gullies run down the sides of the last one's.
      const tangent = (g: number[]) => {
        const a = g[0] * dir.x + g[1] * dir.y + g[2] * dir.z;
        return [g[0] - a * dir.x, g[1] - a * dir.y, g[2] - a * dir.z];
      };
      const g0 = tangent([h[1], h[2], h[3]]);
      const slope0 = Math.hypot(g0[0], g0[1], g0[2]);
      const cut = e.strength * Math.min(slope0, EROSION_SLOPE_CAP);
      const acc: V4 = [0, 0, 0, 0];
      let lambda = e.wavelength;
      let amp = 1;
      let total = 0;
      for (let i = 0; i < e.octaves; i++) {
        const wf = wFine(lambda);
        if (wf > 0) {
          const g = tangent([h[1] + acc[1], h[2] + acc[2], h[3] + acc[3]]);
          // Across the slope: dir × g.
          let wx = dir.y * g[2] - dir.z * g[1];
          let wy = dir.z * g[0] - dir.x * g[2];
          let wz = dir.x * g[1] - dir.y * g[0];
          const wl = Math.max(Math.hypot(wx, wy, wz), 1e-12);
          wx /= wl;
          wy /= wl;
          wz /= wl;
          const f = 1 / lambda;
          const n = stripesd(
            posMod(p.x * f, PERIOD),
            posMod(p.y * f, PERIOD),
            posMod(p.z * f, PERIOD),
            wx,
            wy,
            wz,
          );
          const a = (cut * lambda * amp * wf) / TAU;
          const slopeK = 1 - 2 * EROSION_ROUNDING * n[0];
          acc[0] += (n[0] - EROSION_ROUNDING * n[0] * n[0]) * a;
          for (let c = 1; c < 4; c++) acc[c] += n[c] * slopeK * f * a;
          ridgeMap += n[0] * amp * wf;
        }
        total += amp;
        lambda /= e.lacunarity;
        amp *= e.gain;
      }
      for (let c = 0; c < 4; c++) h[c] += acc[c];
      ridgeMap *= smoothstep(0.02, 0.15, slope0) / total;
    }
    for (let c = 0; c < 4; c++) h[c] += detail[c] * pr.detailHeight;
    // The slope: the gradient across the surface (the part along `dir` is the radial one).
    const along = h[1] * dir.x + h[2] * dir.y + h[3] * dir.z;
    const tx = h[1] - along * dir.x;
    const ty = h[2] - along * dir.y;
    const tz = h[3] - along * dir.z;
    out.height = h[0];
    out.slope = Math.sqrt(tx * tx + ty * ty + tz * tz);
    if (e) out.ridge = ridgeMap;
    return out;
  }
}

/**
 * 3D gradient noise with analytic derivatives: terrainNoise's gnoised, in f64. Takes lattice
 * coordinates already reduced to [0, PERIOD); returns [value, d/dx, d/dy, d/dz].
 */
export function gnoised(x: number, y: number, z: number): V4 {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fy = y - iy;
  const fz = z - iz;
  const ux = fx * fx * fx * (fx * (fx * 6 - 15) + 10);
  const uy = fy * fy * fy * (fy * (fy * 6 - 15) + 10);
  const uz = fz * fz * fz * (fz * (fz * 6 - 15) + 10);
  const dux = fx * fx * 30 * (fx * (fx - 2) + 1);
  const duy = fy * fy * 30 * (fy * (fy - 2) + 1);
  const duz = fz * fz * 30 * (fz * (fz - 2) + 1);
  const x0 = wrap(ix);
  const y0 = wrap(iy);
  const z0 = wrap(iz);
  const x1 = wrap(ix + 1);
  const y1 = wrap(iy + 1);
  const z1 = wrap(iz + 1);
  const ga = hashGrad(x0, y0, z0);
  const gb = hashGrad(x1, y0, z0);
  const gc = hashGrad(x0, y1, z0);
  const gd = hashGrad(x1, y1, z0);
  const ge = hashGrad(x0, y0, z1);
  const gf = hashGrad(x1, y0, z1);
  const gg = hashGrad(x0, y1, z1);
  const gh = hashGrad(x1, y1, z1);
  const dot = (g: number[], a: number, b: number, c: number) => g[0] * a + g[1] * b + g[2] * c;
  const va = dot(ga, fx, fy, fz);
  const vb = dot(gb, fx - 1, fy, fz);
  const vc = dot(gc, fx, fy - 1, fz);
  const vd = dot(gd, fx - 1, fy - 1, fz);
  const ve = dot(ge, fx, fy, fz - 1);
  const vf = dot(gf, fx - 1, fy, fz - 1);
  const vg = dot(gg, fx, fy - 1, fz - 1);
  const vh = dot(gh, fx - 1, fy - 1, fz - 1);
  const k0 = va - vb - vc + vd;
  const k1 = va - vc - ve + vg;
  const k2 = va - vb - ve + vf;
  const k3 = -va + vb + vc - vd + ve - vf - vg + vh;
  const value =
    va +
    ux * (vb - va) +
    uy * (vc - va) +
    uz * (ve - va) +
    ux * uy * k0 +
    uy * uz * k1 +
    uz * ux * k2 +
    k3 * ux * uy * uz;
  const d: number[] = [0, 0, 0];
  // u.yzx·(k0, k1, k2) + u.zxy·(k2, k0, k1) + u.yzx·u.zxy·k3, component by component.
  const cross = [
    uy * k0 + uz * k2 + uy * uz * k3,
    uz * k1 + ux * k0 + uz * ux * k3,
    ux * k2 + uy * k1 + ux * uy * k3,
  ];
  const lin = [vb - va, vc - va, ve - va];
  const du = [dux, duy, duz];
  for (let c = 0; c < 3; c++) {
    const g0 = ga[c] - gb[c] - gc[c] + gd[c];
    const g1 = ga[c] - gc[c] - ge[c] + gg[c];
    const g2 = ga[c] - gb[c] - ge[c] + gf[c];
    const g3 = -ga[c] + gb[c] + gc[c] - gd[c] + ge[c] - gf[c] - gg[c] + gh[c];
    d[c] =
      ga[c] +
      (gb[c] - ga[c]) * ux +
      (gc[c] - ga[c]) * uy +
      (ge[c] - ga[c]) * uz +
      g0 * ux * uy +
      g1 * uy * uz +
      g2 * uz * ux +
      g3 * ux * uy * uz +
      du[c] * (lin[c] + cross[c]);
  }
  return [value, d[0], d[1], d[2]];
}

/** Stripe cells' hash salt and fade floor (as terrainNoise's). */
const STRIPE_SALT = [131, 57, 211];
const STRIPE_FLOOR = 0.3;
const TAU = 2 * Math.PI;

/**
 * Stripes along unit direction (wx, wy, wz): terrainNoise's stripesd, in f64. Takes lattice
 * coordinates already reduced to [0, PERIOD); returns [value, d/dx, d/dy, d/dz].
 */
export function stripesd(x: number, y: number, z: number, wx: number, wy: number, wz: number): V4 {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const f = [x - ix, y - iy, z - iz];
  const u = f.map((v) => v * v * v * (v * (v * 6 - 15) + 10));
  const du = f.map((v) => v * v * 30 * (v * (v - 2) + 1));
  let C = 0;
  let S = 0;
  const dC = [0, 0, 0];
  const dS = [0, 0, 0];
  const w = [wx, wy, wz];
  for (let cz = 0; cz <= 1; cz++) {
    for (let cy = 0; cy <= 1; cy++) {
      for (let cx = 0; cx <= 1; cx++) {
        const o = [cx, cy, cz];
        const phase = hashGrad(
          wrap(ix + cx) + STRIPE_SALT[0],
          wrap(iy + cy) + STRIPE_SALT[1],
          wrap(iz + cz) + STRIPE_SALT[2],
        )[0];
        const t = ((f[0] - cx) * wx + (f[1] - cy) * wy + (f[2] - cz) * wz + phase) * TAU;
        const ws = o.map((on, a) => (on ? u[a] : 1 - u[a]));
        const dws = o.map((on, a) => (on ? du[a] : -du[a]));
        const wt = ws[0] * ws[1] * ws[2];
        const dwt = [dws[0] * ws[1] * ws[2], ws[0] * dws[1] * ws[2], ws[0] * ws[1] * dws[2]];
        const c = Math.cos(t);
        const s = Math.sin(t);
        C += wt * c;
        S += wt * s;
        for (let a = 0; a < 3; a++) {
          dC[a] += dwt[a] * c - w[a] * wt * s * TAU;
          dS[a] += dwt[a] * s + w[a] * wt * c * TAU;
        }
      }
    }
  }
  const m = Math.sqrt(C * C + S * S);
  const inv = 1 / Math.max(m, STRIPE_FLOOR);
  const out: V4 = [C * inv, 0, 0, 0];
  for (let a = 0; a < 3; a++) {
    const dm = (C * dC[a] + S * dS[a]) / Math.max(m, 1e-6);
    out[a + 1] = dC[a] * inv - (m >= STRIPE_FLOOR ? dm * C * inv * inv : 0);
  }
  return out;
}

/** PCG3D integer hash → gradient in [−1, 1]³ (terrainNoise's hashGrad, on u32s). */
export function hashGrad(cx: number, cy: number, cz: number): [number, number, number] {
  let x = (Math.imul(cx >>> 0, 1664525) + 1013904223) >>> 0;
  let y = (Math.imul(cy >>> 0, 1664525) + 1013904223) >>> 0;
  let z = (Math.imul(cz >>> 0, 1664525) + 1013904223) >>> 0;
  x = (x + Math.imul(y, z)) >>> 0;
  y = (y + Math.imul(z, x)) >>> 0;
  z = (z + Math.imul(x, y)) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  y = (y ^ (y >>> 16)) >>> 0;
  z = (z ^ (z >>> 16)) >>> 0;
  x = (x + Math.imul(y, z)) >>> 0;
  y = (y + Math.imul(z, x)) >>> 0;
  z = (z + Math.imul(x, y)) >>> 0;
  const k = 2 / 4294967295;
  return [x * k - 1, y * k - 1, z * k - 1];
}

function wrap(i: number): number {
  return i - Math.floor(i / PERIOD) * PERIOD;
}

function posMod(x: number, m: number): number {
  const r = x % m;
  return r < 0 ? r + m : r;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(Math.max((x - e0) / (e1 - e0), 0), 1);
  return t * t * (3 - 2 * t);
}
